/**
 * ScoreHistoryChart — score over time as a best-so-far "climb" chart.
 *
 * The step line tracks the running BEST score: it only moves when a new
 * point beats every earlier one. Points that fail to beat the best
 * render as smaller muted dots — still hoverable, but visually out of
 * the climb, drawn as a pareto frontier: straight slopes connecting each
 * best-so-far point, flat tail to the latest run. ScatterChart is intentional: its item
 * tooltip keeps same-time points individually hoverable, unlike
 * LineChart's axis-only tooltip. Returns null only with NO points — one
 * point still reads as a climb off the floor, and a group whose only other
 * rows are reference lines needs the chart to show them.
 *
 * The Y axis starts at zero, or at the "random" reference when chance is
 * higher (`scoreFloor`) — on a task where guessing scores 50%, the bottom
 * half of the scale is dead ink.
 *
 * Pass `tasks` and/or `variants` to get a header picker that swaps the
 * whole-group rollup for one member task's own timeline, or for one variant
 * rolled up across every task that ran it. Both are the same shape and both
 * are precomputed by the API, so the chart never re-derives a rollup — it
 * only chooses which series to hand the plot. A caption under the header
 * names whichever series is showing, since "group score over time" stops
 * being an accurate description the moment a pick narrows it.
 *
 * `referenceLines` (random / a baseline / SOTA) render as dotted horizontal
 * rules instead of datapoints — the API already excludes the runs behind
 * them from `points`, so they mark a level without joining the climb. The
 * one labeled "random" also sets the axis floor, so it lands ON the axis
 * unless something scores below it. They
 * follow the picker: each `tasks`/`variants` entry carries its OWN lines,
 * since a reference is pushed per (task, variant) and only rolls up for the
 * whole-group series.
 * Their captions are drawn as one layer so near-equal levels don't overprint.
 */

import { useEffect, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  DefaultZIndexes,
  Legend,
  Rectangle,
  ReferenceLine as ReferenceRule,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
  ZIndexLayer,
  usePlotArea,
  useYAxisScale,
  type BarShapeProps,
  type TooltipContentProps,
} from 'recharts'

import { CHART_INK, CHART_REFERENCE_DASH, CHART_REFERENCE_STROKE, tinyDot } from '@/components/evals/chartStyle'
import { axisLabel, climbLineData, gapToBest, ordinalAxis, ordinalize, scoreDomainWith, scoreFloor, splitClimb, toChartData, type ChartPoint, type GapToBest } from '@/components/evals/climb'
import { formatVariant, type ScoreFormat } from '@/components/evals/format'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { ReferenceLine, ScorePoint, TaskSeries, VariantSeries } from '@/api/generated/model'

// The scaled-score display shared by the tooltip; axis uses `axisLabel` (climb.ts).
function pointLabel(scaled: number, format: ScoreFormat): string {
  return format === 'decimal' ? (scaled / 100).toFixed(3) : `${scaled.toFixed(1)}%`
}

interface ScoreHistoryChartProps {
  title: string
  points: ScorePoint[]
  /** What `points` is, shown under the title while no pick is made. */
  caption: string
  format?: ScoreFormat
  /**
   * Levels to mark with dotted horizontal rules — random, a baseline, SOTA.
   * These belong to the whole-group series; a task or variant pick uses that
   * slice's own `reference_lines` instead.
   */
  referenceLines?: ReferenceLine[]
  /**
   * Per-task timelines of the same metric. When given, the card header gets
   * a picker that swaps `points` (the whole group) for one task's series.
   * Single-point tasks stay pickable — one point still plots as a climb off
   * the floor. Runless tasks are dropped: nothing to draw.
   */
  tasks?: TaskSeries[]
  /**
   * Per-variant timelines — the same rollup as `points`, restricted to one
   * variant's runs across every task that ran it. The other axis of the same
   * picker: `tasks` answers "which task", this answers "which variant".
   * Empty for a single-variant group, where the pick would just be `points`.
   */
  variants?: VariantSeries[]
  /**
   * The tasks a variant pick rolls up: "every task", or "every held-out
   * test" for a group scored on its held-out tests (TRA-1367).
   */
  variantScope?: string
}

export interface ScoreSeries {
  variant: string
  points: ScorePoint[]
}

/**
 * Per-series line color: fixed brand-family palette (ink, olive, sage,
 * dusty teal, clay, muted red) instead of saturated hue rotation —
 * series stay distinguishable while the chart reads ink-on-paper
 * (VISUAL_DESIGN_PRINCIPLES §3). Literal-color exception: semantic
 * tokens only offer one chart color.
 */
const SERIES_COLORS = [
  CHART_INK, // neutral olive
  'hsl(227 7% 24%)', // gunmetal ink
  'hsl(126 12% 42%)', // sage
  'hsl(182 26% 39%)', // dusty teal
  'hsl(24 30% 45%)', // clay
  'hsl(0 35% 45%)', // muted red
] as const

function seriesColor(index: number): string {
  const color = SERIES_COLORS[index % SERIES_COLORS.length]
  if (color === undefined) {
    throw new Error(`unreachable: series color index ${index.toString()}`)
  }
  return color
}


interface PointTooltipProps {
  tooltip: TooltipContentProps
  format?: ScoreFormat
  /** Reports whether a dot tooltip is showing — lets the host chart hide
   * the reference hover box so the two popovers never overlap. */
  onActiveChange?: (active: boolean) => void
}

function PointTooltip({
  tooltip,
  format = 'percent',
  onActiveChange,
}: PointTooltipProps): React.JSX.Element | null {
  const { active, payload } = tooltip
  const entry = payload.find((item) => item.dataKey === 'x')
  const rawScore = payload.find((item) => item.dataKey === 'score')?.value
  const point =
    entry !== undefined && typeof rawScore === 'number'
      ? { datum: entry.payload as ChartPoint, score: rawScore }
      : null
  const showing = active && point !== null
  useEffect(() => {
    onActiveChange?.(showing)
    return () => {
      onActiveChange?.(false)
    }
  }, [showing, onActiveChange])
  if (!active || point === null) return null
  const { datum, score } = point
  const timestamp = datum.date

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="rounded-lg border border-border bg-card p-2 text-xs shadow-sm"
    >
      <p className="text-muted-foreground">{new Date(timestamp).toLocaleString()}</p>
      {datum.variant !== undefined && (
        <p className="mt-1 font-mono text-muted-foreground">{formatVariant(datum.variant)}</p>
      )}
      <p className="mt-1 font-medium text-foreground">{datum.model}</p>
      <p className="mt-1 text-foreground">
        Score <span className="font-medium tabular-nums">{pointLabel(score, format)}</span>
      </p>
    </div>
  )
}

function ChartAxes({
  timestamps,
  format,
  referenceScores = [],
  floor = 0,
}: {
  timestamps: number[]
  format: ScoreFormat
  /** Scaled [0,100] reference scores the Y max must clear. */
  referenceScores?: number[]
  /** Scaled [0,100] Y min — 0, or chance when a "random" line is set. */
  floor?: number
}): React.JSX.Element {
  return (
    <>
      {/* Full square grid, faint solid warm hairlines — mock chart style. */}
      <CartesianGrid stroke="hsl(var(--border) / 0.6)" />
      {/* Ordinal time axis: one evenly spaced slot per distinct run
          timestamp, so idle gaps between runs don't dominate the chart.
          tickMargin drops the date labels clear of the plot — a reference
          line sitting at or near zero prints its caption on the axis. */}
      <XAxis
        dataKey="x"
        name="Date"
        type="number"
        domain={ordinalAxis(timestamps).domain}
        ticks={ordinalAxis(timestamps).ticks}
        interval="preserveStartEnd"
        tickFormatter={ordinalAxis(timestamps).format}
        tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
        tickLine={false}
        axisLine={false}
        tickMargin={12}
      />
      <YAxis
        dataKey="score"
        name="Score"
        domain={[floor, scoreDomainWith(referenceScores)]}
        tickFormatter={(value: number) => axisLabel(value, format)}
        tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
        tickLine={false}
        axisLine={false}
        width={48}
      />
    </>
  )
}

/** Popover drop below the cursor — matches recharts' `insideTopRight` label. */
const CAPTION_DROP = 13
/** Right inset, same as the recharts label offset the popover replaces. */
const CAPTION_INSET = 5
/** How close (px) the cursor must be to a rule for the popover to show. */
const CAPTION_HOVER_RADIUS = 14

/**
 * Hovering near a dashed rule pops a tooltip-style box listing every rule
 * within reach (references cluster — protege: 84.5% and 86.5% — so one box
 * lists them all). Replaces on-chart caption text, which sat on the climb
 * line and hid the dots whenever scores ran reference-high. Pixel math
 * needs the live scale, so this must render inside the chart; the box is
 * HTML via foreignObject so it matches PointTooltip's styling exactly.
 */
function ReferenceHoverPopover({
  references,
  cursorY,
  format,
}: {
  references: { label: string; y: number }[]
  /** Cursor Y within the chart wrapper, or null when not hovering. */
  cursorY: number | null
  format: ScoreFormat
}): React.JSX.Element | null {
  const plotArea = usePlotArea()
  const yScale = useYAxisScale()
  if (plotArea === undefined || yScale === undefined || cursorY === null) return null
  const hits = references
    .flatMap((line) => {
      const pixel = yScale(line.y)
      if (pixel === undefined || Math.abs(pixel - cursorY) > CAPTION_HOVER_RADIUS) return []
      return [line]
    })
    .sort((a, b) => b.y - a.y)
  if (hits.length === 0) return null
  const BOX_WIDTH = 200
  const ROW_HEIGHT = 18
  const PADDING = 8
  const boxHeight = hits.length * ROW_HEIGHT + PADDING * 2
  const x = Math.max(plotArea.x, plotArea.x + plotArea.width - BOX_WIDTH - CAPTION_INSET)
  // Below the cursor unless that would leave the plot; then above — and
  // always clamped fully inside the plot so the box never clips.
  const below = cursorY + CAPTION_DROP
  const flipped =
    below + boxHeight > plotArea.y + plotArea.height ? cursorY - CAPTION_DROP - boxHeight : below
  const y = Math.min(
    Math.max(flipped, plotArea.y),
    plotArea.y + plotArea.height - boxHeight
  )
  return (
    <ZIndexLayer zIndex={DefaultZIndexes.label}>
      <foreignObject
        x={x}
        y={y}
        width={BOX_WIDTH}
        height={boxHeight}
        style={{ overflow: 'visible', pointerEvents: 'none' }}
      >
        <div className="rounded-lg border border-border bg-card p-2 text-xs shadow-sm">
          {hits.map((line) => (
            <p key={line.label} className="flex justify-between gap-4">
              <span className="truncate text-muted-foreground">{line.label}</span>
              <span className="tabular-nums text-foreground">{pointLabel(line.y, format)}</span>
            </p>
          ))}
        </div>
      </foreignObject>
    </ZIndexLayer>
  )
}

/** Muted small-dot series for runs that didn't beat the best-so-far. */
function BelowBestScatter({ data }: { data: ChartPoint[] }): React.JSX.Element {
  return (
    <Scatter
      name="Below best"
      data={data}
      zAxisId="below"
      fill="hsl(var(--muted-foreground) / 0.5)"
      shape={tinyDot(4, 'hsl(var(--muted-foreground) / 0.5)')}
      legendType="none"
      isAnimationActive={false}
    />
  )
}

/**
 * The climb line as a DOTLESS series spanning the full time range —
 * separate from the best-dot series so neither synthetic end (the origin on
 * the axis floor, and the extension drawn when later runs never beat the
 * best) gets a marker.
 *
 * The origin briefly carried an open circle, to keep the line from leaving
 * the floor as a bare corner. It read as a model scoring whatever the floor
 * happened to be (TRA-1206): being unhoverable is not a signal anyone
 * discovers, and hollow-vs-filled distinguishes two kinds of DATA, not data
 * from non-data. A bare corner beats a mark that reads as a measurement.
 */
function ClimbLineScatter({
  best,
  lastX,
  color,
  strokeWidth,
  floor = 0,
}: {
  best: ChartPoint[]
  lastX: number
  color: string
  strokeWidth: number
  /** Where the synthetic origin sits — must match the Y axis' min. */
  floor?: number
}): React.JSX.Element {
  return (
    <Scatter
      data={climbLineData(best, lastX, floor)}
      shape={() => <g />}
      tooltipType="none"
      legendType="none"
      line={{ stroke: color, strokeWidth }}
      lineJointType="linear"
      isAnimationActive={false}
    />
  )
}

/**
 * At or below this many BARS — runs plus reference levels, since both
 * become bars — the climb renders as a bar chart instead of a line.
 *
 * A line between two points draws a slope nobody measured: it reads as a
 * trend when it is really just two numbers. Bars compare a handful of
 * numbers honestly; a line earns its keep once there is a shape to see.
 * References count because they occupy the same axis and the same
 * attention — three runs against two references is a crowded bar chart,
 * not a comparison.
 */
const BAR_CHART_MAX_BARS = 3

/**
 * One bar-mode row. A row is EITHER a run or a reference level, never both —
 * two separate dataKeys is how the two get different fills without recharts'
 * deprecated per-cell `Cell`.
 */
/** Pixels a zero-score bar still occupies, so it can be seen and hovered. */
const BAR_MIN_HEIGHT = 3

/**
 * ONE series, with the run/reference distinction carried on the row and
 * resolved at paint time.
 *
 * Two series — one per dataKey — is the obvious way to get two fills, but
 * every row then has an empty slot in the other series. Side by side that
 * empty slot still reserves width (uneven spacing); sharing a `stackId`
 * fixes the spacing but makes recharts resolve the empty slot to 0 inside
 * the stack, which `minPointSize` then floors into a 3px nub in the wrong
 * colour. One series has no empty slots to mis-render.
 */
interface BarRow {
  label: string
  score: number
  /** Reference rows are levels to beat, not runs — drawn in the muted tone. */
  isReference: boolean
  /** The originating run, for the tooltip. Absent on reference rows. */
  point?: ChartPoint
}

function toBarRows(
  data: ChartPoint[],
  references: { label: string; y: number }[],
): BarRow[] {
  return [
    ...data.map((point) => ({
      label: point.model,
      score: point.score,
      isReference: false,
      point,
    })),
    // Levels to beat sit to the right of the runs, so the eye reads
    // "what we scored" before "what we're chasing".
    ...references.map((line) => ({
      label: line.label,
      score: line.y,
      isReference: true,
    })),
  ]
}

/**
 * Per-row fill (since `fill` takes no function), plus the gap sketch
 * (TRA-993): every TRAILING bar gets a dotted outline continuing up to
 * the tallest bar's level, so each bar's annotation is its own distance
 * to first place — the actual acceptance bar — not a distance to 100%.
 * When the trained model takes the lead the same rule lands a sketch on
 * the model it just passed, which reads as "ahead by this much".
 *
 * An outline rather than a second fill: filled area reads as a quantity
 * someone measured, and this one was never measured. Neutral ink, no
 * success/destructive channel — a gap is not a verdict, and second place
 * is not an error state.
 *
 * The sketch is placed off the Y SCALE, never off the drawn bar. Scaling
 * `props.height` by a score ratio looks equivalent and isn't: `minPointSize`
 * floors a near-floor bar at 3px, so those 3px stop being proportional to
 * anything, and a run exactly ON the floor would divide by zero. Asking the
 * axis where a score lives has neither failure.
 */
/** SVG pattern id for the faint diagonal fill inside the gap sketch. */
const GAP_HATCH_ID = 'score-bar-gap-hatch'

/** Sketch height (px) below which the in-sketch gap label is dropped. */
const GAP_LABEL_MIN_HEIGHT = 16

/** Sketch height (px) at which the label gains its "behind" second line. */
const GAP_LABEL_TWO_LINE_HEIGHT = 34

/**
 * Slight diagonal texture inside the sketch, so the gap reads as a
 * region rather than an empty box the eye skips. Kept well under the dashed
 * outline's weight: the outline carries the shape, the hatch only fills it.
 * Same neutral ink as the bar — this qualifies the bar's own quantity, it
 * doesn't introduce a second one. Emitted once per chart; SVG ids are
 * document-scoped and two charts declaring an identical pattern under one
 * id is harmless.
 */
function GapHatch(): React.JSX.Element {
  return (
    <defs>
      <pattern
        id={GAP_HATCH_ID}
        width={6}
        height={6}
        patternUnits="userSpaceOnUse"
        patternTransform="rotate(45)"
      >
        <line x1={0} y1={0} x2={0} y2={6} stroke={CHART_INK} strokeWidth={1} opacity={0.58} />
      </pattern>
    </defs>
  )
}

function BarRect({
  gap,
  format,
  ...props
}: BarShapeProps & { gap: GapToBest | null; format: ScoreFormat }): React.JSX.Element {
  const yScale = useYAxisScale()
  const row = props.payload as BarRow | undefined
  const fill = row?.isReference === true ? CHART_REFERENCE_STROKE : CHART_INK
  const bar = <Rectangle {...props} radius={[2, 2, 0, 0]} fill={fill} />
  // The sketch draws on every trailing bar — references included, since
  // a passed reference is exactly where "ahead by this much" belongs.
  // Trailing is decided by SCORE, never by label: the same model can
  // appear twice in a small chart (re-run on two dates), and the lower
  // of the pair still trails the leader.
  if (row === undefined || yScale === undefined || gap === null) return bar
  if (row.score >= gap.bestScore) return bar
  const top = yScale(gap.bestScore)
  const bottom = yScale(row.score)
  if (top === undefined || bottom === undefined || bottom <= top) return bar
  return (
    <g>
      {bar}
      {/* ONE dashed outline does the whole sketch: its bottom edge is the
          rule at this bar's score, its top edge the rule at the leader's
          score, its sides the gap between them. Inset half a pixel so the
          1px stroke sits inside the bar's own width. */}
      <rect
        x={props.x + 0.5}
        y={top}
        width={props.width - 1}
        height={bottom - top}
        fill={`url(#${GAP_HATCH_ID})`}
        stroke={CHART_INK}
        strokeWidth={1}
        strokeDasharray="3 3"
        opacity={0.75}
      />
      {/* The number itself, when the sketch is tall enough to hold it —
          the tooltip still names it either way. A taller sketch also fits
          the word "behind", so the region reads as a sentence, not a
          stray figure. */}
      {bottom - top >= GAP_LABEL_MIN_HEIGHT && (
        <text
          x={props.x + props.width / 2}
          y={(top + bottom) / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={11}
          fill={CHART_INK}
          stroke="hsl(var(--background))"
          strokeWidth={3}
          paintOrder="stroke"
        >
          {bottom - top >= GAP_LABEL_TWO_LINE_HEIGHT ? (
            <>
              <tspan x={props.x + props.width / 2} dy={-6}>
                {pointLabel(gap.bestScore - row.score, format)}
              </tspan>
              <tspan x={props.x + props.width / 2} dy={13}>
                behind
              </tspan>
            </>
          ) : (
            pointLabel(gap.bestScore - row.score, format)
          )}
        </text>
      )}
    </g>
  )
}

/**
 * The hovered bar's standing against the leader (TRA-993): "Behind
 * <leader> by X" on everything else, "Ahead of <runner-up> by X" on the
 * leader itself. Null on the leader when nothing trails it to name.
 */
function standingLabel(
  row: BarRow,
  gap: GapToBest | null,
  format: ScoreFormat
): string | null {
  if (gap === null) return null
  // Leadership is decided by SCORE, not label — a duplicate label's lower
  // bar still trails (same reasoning as the sketch in BarRect).
  if (row.score >= gap.bestScore) {
    // Tied for first: no margin worth claiming.
    if (gap.gap <= 0) return null
    return `Ahead of ${gap.runnerUpLabel} by ${pointLabel(gap.gap, format)}`
  }
  // Every trailing bar reports its OWN distance to the leader.
  return `Behind ${gap.bestLabel} by ${pointLabel(gap.bestScore - row.score, format)}`
}

/** Bar-mode tooltip — same content as PointTooltip, different payload shape. */
function BarTooltip({
  tooltip,
  format,
  gap,
}: {
  tooltip: TooltipContentProps
  format: ScoreFormat
  gap: GapToBest | null
}): React.JSX.Element | null {
  const { active, payload } = tooltip
  const row = payload[0]?.payload as BarRow | undefined
  if (!active || row === undefined) return null
  const datum = row.point
  const standing = standingLabel(row, gap, format)
  return (
    <div
      role="status"
      aria-live="polite"
      className="rounded-lg border border-border bg-card p-2 text-xs shadow-sm"
    >
      {datum === undefined ? (
        <>
          <p className="text-muted-foreground">Reference</p>
          <p className="mt-1 font-medium text-foreground">{row.label}</p>
          <p className="mt-1 text-foreground">
            Score{' '}
            <span className="font-medium tabular-nums">{pointLabel(row.score, format)}</span>
          </p>
          {standing !== null && <p className="mt-1 text-muted-foreground">{standing}</p>}
        </>
      ) : (
        <>
          <p className="text-muted-foreground">{new Date(datum.date).toLocaleString()}</p>
          {datum.variant !== undefined && (
            <p className="mt-1 font-mono text-muted-foreground">{formatVariant(datum.variant)}</p>
          )}
          <p className="mt-1 font-medium text-foreground">{datum.model}</p>
          <p className="mt-1 text-foreground">
            Score{' '}
            <span className="font-medium tabular-nums">{pointLabel(datum.score, format)}</span>
          </p>
          {/* Names the number the dotted sketch draws, so the reader
              doesn't have to measure it off the axis. */}
          {standing !== null && <p className="mt-1 text-muted-foreground">{standing}</p>}
        </>
      )}
    </div>
  )
}

/**
 * The few-runs view: one bar per run in ink, plus one bar per reference in
 * the muted reference tone. A dashed rule is the LINE chart's idiom — it
 * marks a level across a time axis. With bars there is no time axis, and a
 * level you are chasing compares most honestly as another bar of the same
 * kind, sitting beside what you actually scored.
 */
function ClimbBars({
  data,
  format,
  references,
  floor,
  hoverable,
}: {
  data: ChartPoint[]
  format: ScoreFormat
  references: { label: string; y: number }[]
  floor: number
  hoverable: boolean
}): React.JSX.Element {
  const rows = toBarRows(data, references)
  const gap = gapToBest(rows)
  return (
    <ResponsiveContainer width="100%" aspect={2}>
      <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <GapHatch />
        <CartesianGrid stroke="hsl(var(--border) / 0.6)" vertical={false} />
        <XAxis
          dataKey="label"
          type="category"
          tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
          tickLine={false}
          axisLine={false}
          tickMargin={12}
        />
        <YAxis
          // The gap sketch tops out at the tallest BAR's level, so an axis
          // that clears every reference already clears the sketch too.
          domain={[floor, scoreDomainWith(references.map((line) => line.y))]}
          tickFormatter={(value: number) => axisLabel(value, format)}
          tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
          tickLine={false}
          axisLine={false}
          width={48}
        />
        {hoverable && (
          <Tooltip
            cursor={{ fill: 'hsl(var(--muted-foreground) / 0.08)' }}
            isAnimationActive={false}
            content={(tooltip) => <BarTooltip tooltip={tooltip} format={format} gap={gap} />}
          />
        )}
        {/* minPointSize keeps a zero (or near-zero) score visible as a
            sliver instead of nothing at all. A bar with no height is
            indistinguishable from a missing run, and there is nothing to
            hover to find out which it was. The tooltip reads the row, not
            the rendered height, so it still reports the true 0.0%. */}
        <Bar
          dataKey="score"
          maxBarSize={56}
          minPointSize={BAR_MIN_HEIGHT}
          isAnimationActive={false}
          shape={(props: BarShapeProps) => <BarRect {...props} gap={gap} format={format} />}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}

/**
 * The score-over-time climb plot, shared by the group page, the overview
 * cards, and matrix-cell tooltips. `hoverable=false` drops the recharts
 * tooltip (for use inside another popup). Pass raw ScorePoints through
 * `toChartData` first.
 *
 * Three bars or fewer (runs + references) render as bars — see
 * BAR_CHART_MAX_BARS.
 */
export function HistoryPlot({
  data,
  format = 'percent',
  referenceLines = [],
  hoverable = true,
}: {
  data: ChartPoint[]
  format?: ScoreFormat
  referenceLines?: ReferenceLine[]
  hoverable?: boolean
}): React.JSX.Element | null {
  // The reference hover box shows only near a rule — and never while a dot
  // tooltip is up, so the two popovers can't overlap.
  const [cursorY, setCursorY] = useState<number | null>(null)
  const [dotActive, setDotActive] = useState(false)
  // Reference scores arrive in [0,1]; the axis is the [0,100] climb scale.
  const references = referenceLines.map((line) => ({ ...line, y: line.score * 100 }))
  // Nothing at all to plot.
  if (data.length === 0 && references.length === 0) return null
  // Runless but referenced — every run in the group is a reference. The
  // levels to beat still say what the group measures, so draw them as
  // bars. Bars are the only shape this state can take, whatever the
  // count: the scatter's time axis is defined off run timestamps.
  if (data.length === 0) {
    return (
      <ClimbBars
        data={data}
        format={format}
        references={references}
        floor={scoreFloor(references, data)}
        hoverable={hoverable}
      />
    )
  }
  const { series, timestamps } = ordinalize([data])
  const ordinal = series[0] ?? []
  const { best, below } = splitClimb(ordinal)
  // Extend the flat tail through the trailing day-after slot.
  const lastX = timestamps.length
  // Chance is the real bottom of a scored chart when it's set — the climb
  // starts there too, or the origin would pull the domain back to zero.
  const floor = scoreFloor(references, ordinal)
  // Too few marks for a line to mean anything — compare the numbers
  // instead. References count: they render as bars too.
  if (data.length + references.length <= BAR_CHART_MAX_BARS) {
    return (
      <ClimbBars
        data={data}
        format={format}
        references={references}
        floor={floor}
        hoverable={hoverable}
      />
    )
  }
  return (
    // 2:1 width:height — all eval charts share this aspect ratio.
    <div
      onMouseMove={(event) => {
        setCursorY(event.clientY - event.currentTarget.getBoundingClientRect().top)
      }}
      onMouseLeave={() => {
        setCursorY(null)
      }}
    >
    <ResponsiveContainer width="100%" aspect={2}>
      <ScatterChart margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <ChartAxes
          timestamps={timestamps}
          format={format}
          referenceScores={references.map((line) => line.y)}
          floor={floor}
        />
        {/* Near-dotless mock style: dots stay only as tiny hover targets. */}
        <ZAxis range={[10, 10]} />
        <ZAxis zAxisId="below" range={[8, 8]} />
        {/* The dot popover also lists every reference value — when scores
            run reference-high the dash cluster sits right on the climb
            line, so the numbers must be readable without eyeballing rules. */}
        {hoverable && (
          <Tooltip
            shared={false}
            cursor={false}
            isAnimationActive={false}
            content={(tooltip) => (
              <PointTooltip tooltip={tooltip} format={format} onActiveChange={setDotActive} />
            )}
          />
        )}
        {/* Drawn before the series so the ink climb line stays on top. The Y
            domain already clears every line (`scoreDomainWith`), so recharts'
            default discard-on-overflow never fires. Captions are a separate
            layer (`ReferenceCaptions`) so they can dodge each other. */}
        {references.map((line) => (
          <ReferenceRule
            key={line.label}
            y={line.y}
            stroke={CHART_REFERENCE_STROKE}
            strokeDasharray={CHART_REFERENCE_DASH}
            strokeWidth={1}
          />
        ))}
        <ReferenceHoverPopover references={references} cursorY={dotActive ? null : cursorY} format={format} />
        <ClimbLineScatter
          best={best}
          lastX={lastX}
          color={CHART_INK}
          strokeWidth={1.5}
          floor={floor}
        />
        <Scatter
          name="Best so far"
          data={best}
          fill={CHART_INK}
          shape={tinyDot(4.5, CHART_INK)}
          isAnimationActive={false}
        />
        <BelowBestScatter data={below} />
      </ScatterChart>
    </ResponsiveContainer>
    </div>
  )
}

function ChartCard({
  title,
  caption,
  control,
  children,
}: {
  title: string
  /** Optional sentence under the header describing what's plotted. */
  caption?: string
  /** Optional header-right control (e.g. the series picker). */
  control?: React.ReactNode
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
          {control}
        </div>
      </CardHeader>
      <CardContent>
        {caption !== undefined && <p className="mb-2 text-xs text-muted-foreground">{caption}</p>}
        <div className="w-full">{children}</div>
      </CardContent>
    </Card>
  )
}

/**
 * The picker's option values. Task slugs and variant labels are namespaced
 * so neither can collide with the whole-group pick or with each other — and
 * so the unlabeled variant (empty string) still has a non-empty value, which
 * radix requires.
 */
const ALL_SERIES = 'all'
const taskValue = (slug: string): string => `task:${slug}`
const variantValue = (variant: string): string => `variant:${variant}`

/**
 * The two ways to narrow the group rollup, resolved to one series. Both are
 * precomputed by the API and have identical shape; only the caption and the
 * menu heading differ, so they collapse to one type here.
 */
interface SeriesPick {
  value: string
  /** Menu label, and the subject of the caption sentence. */
  label: string
  history: ScorePoint[]
  referenceLines: ReferenceLine[]
  /** Caption for this pick — a task IS the series, a variant rolls up across tasks. */
  caption: string
}

export function ScoreHistoryChart({
  title,
  points,
  caption,
  format = 'percent',
  referenceLines = [],
  tasks = [],
  variants = [],
  variantScope = 'every task',
}: ScoreHistoryChartProps): React.JSX.Element | null {
  const [selected, setSelected] = useState<string>(ALL_SERIES)
  // Runless slices are dropped: there is nothing to draw. A single point is
  // still plottable — it reads as a climb off the floor.
  const taskPicks: SeriesPick[] = tasks
    .filter((entry) => entry.history.length > 0)
    .map((entry) => ({
      value: taskValue(entry.slug),
      label: entry.display_name,
      history: entry.history,
      referenceLines: entry.reference_lines ?? [],
      caption: `${entry.display_name}'s score at each run date.`,
    }))
  const variantPicks: SeriesPick[] = variants
    .filter((entry) => entry.history.length > 0)
    .map((entry) => ({
      value: variantValue(entry.variant),
      label: formatVariant(entry.variant),
      history: entry.history,
      referenceLines: entry.reference_lines ?? [],
      caption: `The ${formatVariant(entry.variant)} variant rolled up across ${variantScope} that ran it.`,
    }))
  const picks = [...taskPicks, ...variantPicks]
  // Falls back to the group rollup whenever the pick isn't in the current
  // data (the default, or a slice that vanished on refetch) — self-heals
  // with no state write.
  const picked = picks.find((entry) => entry.value === selected)
  // References alone keep the card: the levels to beat are worth showing
  // before the first scored run lands.
  if (points.length === 0 && referenceLines.length === 0) return null
  return (
    <ChartCard
      title={title}
      // The title names the chart; this names the SERIES, which the picker
      // changes out from under it.
      caption={
        picked?.caption ??
        (points.length === 0
          ? 'No scored runs yet. The chart shows only the levels to beat.'
          : caption)
      }
      control={
        picks.length > 0 ? (
          <Select value={picked === undefined ? ALL_SERIES : selected} onValueChange={setSelected}>
            <SelectTrigger
              aria-label="Series shown in the score chart"
              className="h-8 w-56 text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* Not labeled "average": a group rolls up by mean OR max. */}
              <SelectItem value={ALL_SERIES}>Whole group</SelectItem>
              {/* Headings only once both axes are offered — a lone "Tasks"
                  caption over the only section is noise. */}
              {[
                { heading: 'By task', options: taskPicks },
                { heading: 'By variant', options: variantPicks },
              ]
                .filter((section) => section.options.length > 0)
                .map((section) => (
                  <SelectGroup key={section.heading}>
                    {taskPicks.length > 0 && variantPicks.length > 0 && (
                      <SelectLabel>{section.heading}</SelectLabel>
                    )}
                    {section.options.map((entry) => (
                      <SelectItem key={entry.value} value={entry.value}>
                        {entry.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
            </SelectContent>
          </Select>
        ) : undefined
      }
    >
      <HistoryPlot
        data={toChartData(picked?.history ?? points)}
        format={format}
        // A reference is pushed per (task, variant), so its line moves with
        // the pick — the group-wide rollup only belongs on the whole-group
        // series.
        referenceLines={picked?.referenceLines ?? referenceLines}
      />
    </ChartCard>
  )
}

interface VariantHistoryChartProps {
  title: string
  series: ScoreSeries[]
  format?: ScoreFormat
}

/** Mute a series color to a faint ghost of itself. */
function mutedColor(color: string): string {
  return color.replace(')', ' / 0.15)')
}

/**
 * VariantHistoryChart — one step line PER variant, so each dot connects
 * to the same variant's next run instead of the flattened staircase a
 * single series produces when several variants share each upload date.
 * Clicking a legend item mutes/unmutes that series to filter the view.
 */
export function VariantHistoryChart({ title, series, format = 'percent' }: VariantHistoryChartProps): React.JSX.Element | null {
  const [muted, setMuted] = useState<ReadonlySet<string>>(new Set())
  const drawable = series.filter((entry) => entry.points.length >= 2)
  if (drawable.length === 0) return null
  const { series: ordinalSeries, timestamps } = ordinalize(
    drawable.map((entry) => toChartData(entry.points, entry.variant))
  )
  const splits = drawable.map((entry, index) => {
    const data = ordinalSeries[index]
    if (data === undefined) {
      throw new Error(`ordinalize dropped series ${entry.variant}`)
    }
    return { variant: entry.variant, lastX: timestamps.length, ...splitClimb(data) }
  })
  const toggle = (name: string): void => {
    setMuted((prev) => {
      const next = new Set(prev)
      if (next.has(name)) {
        next.delete(name)
      } else {
        next.add(name)
      }
      return next
    })
  }
  const colorFor = (variant: string, index: number): string =>
    muted.has(formatVariant(variant)) ? mutedColor(seriesColor(index)) : seriesColor(index)
  return (
    <ChartCard title={title}>
      <ResponsiveContainer width="100%" aspect={2}>
        <ScatterChart margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <ChartAxes timestamps={timestamps} format={format} />
          <Tooltip
            shared={false}
            cursor={false}
            isAnimationActive={false}
            content={(tooltip) => <PointTooltip tooltip={tooltip} format={format} />}
          />
          <Legend
            wrapperStyle={{ fontSize: 12, cursor: 'pointer' }}
            onClick={(item) => {
              if (typeof item.value === 'string') toggle(item.value)
            }}
            formatter={(value: string) => (
              <span
                style={
                  muted.has(value)
                    ? { color: 'hsl(var(--muted-foreground) / 0.5)' }
                    : undefined
                }
              >
                {value}
              </span>
            )}
          />
          {/* Smaller symbol area for below-best dots (default is 64). */}
          <ZAxis zAxisId="below" range={[24, 24]} />
          {splits.map(({ variant, best, lastX }, index) => (
            <ClimbLineScatter
              key={`${variant}-line`}
              best={best}
              lastX={lastX}
              color={colorFor(variant, index)}
              strokeWidth={1.5}
            />
          ))}
          {splits.map(({ variant, best }, index) => (
            <Scatter
              key={variant}
              name={formatVariant(variant)}
              data={best}
              fill={colorFor(variant, index)}
              shape={tinyDot(4.5, colorFor(variant, index))}
              isAnimationActive={false}
            />
          ))}
          {splits.map(({ variant, below }) =>
            below.length > 0 && !muted.has(formatVariant(variant)) ? (
              <BelowBestScatter key={`${variant}-below`} data={below} />
            ) : null
          )}
        </ScatterChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}
